import { formatBytes } from "@cva/shared";
import { Upload, X } from "lucide-react";
import { Progress } from "../components/ui/card";
import { formatRemaining, uploadRate, useNow } from "../lib/activity";
import { cancelUpload, type UploadItem, useUploads } from "../lib/uploads";
import { cn } from "../lib/utils";
import { useEnv } from "./env";

/** Running uploads of the organization whose type the panel shows. */
function useRunningUploads(accept: (mime: string) => boolean): UploadItem[] {
  const env = useEnv();
  return useUploads().filter(
    (item) => item.orgId === env.orgId && item.status === "uploading" && accept(item.mime),
  );
}

function UploadProgress({ item }: { item: UploadItem }) {
  const now = useNow(true);
  const rate = uploadRate(item, now);
  const percent = Math.floor((item.uploaded / Math.max(item.size, 1)) * 100);
  return (
    <>
      <Progress className="h-1.5" value={percent} />
      <span className="block truncate text-[10px] text-zinc-500">
        {percent} % · {formatBytes(item.uploaded)} von {formatBytes(item.size)}
        {rate ? ` · ${formatRemaining(rate.remainingMs)}` : ""}
      </span>
    </>
  );
}

/**
 * Placeholders for files that are still being uploaded, at the top of a
 * media panel: right where the user clicked "Hochladen".
 */
export function UploadingTiles({
  accept,
  layout,
}: {
  accept: (mime: string) => boolean;
  layout: "tiles" | "rows";
}) {
  const uploads = useRunningUploads(accept);
  if (uploads.length === 0) return null;
  return (
    <div className={cn("mb-2", layout === "tiles" ? "grid grid-cols-2 gap-2" : "space-y-1.5")}>
      {uploads.map((item) => (
        <div
          key={item.id}
          className={cn(
            "relative flex flex-col justify-end gap-1 rounded-lg border border-dashed border-brand-500/40 bg-brand-50 p-2",
            layout === "tiles" && "aspect-[9/12]",
          )}
        >
          <button
            type="button"
            onClick={() => cancelUpload(item.id)}
            className="absolute top-1 right-1 rounded p-0.5 text-zinc-400 hover:bg-white hover:text-zinc-700"
            aria-label={`Upload von ${item.name} abbrechen`}
            title="Upload abbrechen"
          >
            <X className="size-3.5" />
          </button>
          {layout === "tiles" ? (
            <Upload className="mx-auto mb-auto mt-6 size-6 text-brand-500" />
          ) : null}
          <span className="flex items-center gap-1.5 pr-5 text-xs font-medium text-zinc-800">
            {layout === "rows" ? <Upload className="size-3.5 shrink-0 text-brand-500" /> : null}
            <span className="truncate" title={item.name}>
              {item.name}
            </span>
          </span>
          <UploadProgress item={item} />
        </div>
      ))}
    </div>
  );
}
